import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useRegistrosTab } from "@/hooks/useRegistrosTab";
import { Download, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  green: "bg-emerald-100 text-emerald-700",
  red: "bg-red-100 text-red-700",
  amber: "bg-amber-100 text-amber-700",
  orange: "bg-orange-100 text-orange-700",
  blue: "bg-blue-100 text-blue-700",
  muted: "bg-muted text-muted-foreground",
};

function getRecordStatus(r) {
  if (r.manually_corrected) return { label: "Corrigido", color: "blue", detail: null };
  if (!r.clock_out && r.status === "open") {
    const hoursOpen = (Date.now() - new Date(r.clock_in)) / 3600000;
    if (hoursOpen > 14)
      return { label: "Saída faltando", color: "red", detail: `Entrada há ${hoursOpen.toFixed(0)}h sem registo de saída` };
    return { label: "Em serviço", color: "green", detail: null };
  }
  if (r.clock_out && new Date(r.clock_out) < new Date(r.clock_in))
    return { label: "Erro horário", color: "red", detail: "Saída anterior à entrada — verificar manualmente" };
  if ((r.hours_worked || 0) > 12)
    return { label: "Jornada longa", color: "amber", detail: `${r.hours_worked?.toFixed(1)}h trabalhadas — acima do limite recomendado` };
  if (r.clock_in_within_radius === false)
    return { label: "Fora do local", color: "orange", detail: r.clock_in_distance_m ? `GPS: ${r.clock_in_distance_m}m do local de trabalho` : "GPS indica fora do raio permitido" };
  if ((r.flags || []).includes("gps_low_accuracy"))
    return { label: "GPS impreciso", color: "amber", detail: r.clock_in_accuracy ? `Precisão: ±${Math.round(r.clock_in_accuracy)}m` : "Sinal GPS fraco no momento do registo" };
  if ((r.flags || []).includes("suspicious_pattern"))
    return { label: "Padrão suspeito", color: "red", detail: "Múltiplos registos de entrada em curto espaço de tempo" };
  return { label: "OK", color: "muted", detail: null };
}

function fmtTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(iso) {
  if (!iso) return "-";
  return iso.slice(0, 10).split("-").reverse().join("/");
}

function toLocalInput(iso) {
  if (!iso) return "";
  return iso.slice(0, 16);
}

export function RegistrosTab() {
  const { records, members, filterMember, setFilterMember, startDate, setStartDate, endDate, setEndDate, deleteRecord, exportCSV, editRecord } = useRegistrosTab();
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", notes: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const openEdit = (r) => {
    setEditTarget(r);
    setEditForm({ clock_in: toLocalInput(r.clock_in), clock_out: toLocalInput(r.clock_out), notes: r.notes || "", reason: "" });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editForm.reason.trim()) { toast.error("Motivo da correção é obrigatório"); return; }
    setSaving(true);
    const ok = await editRecord(editTarget.id, editForm);
    setSaving(false);
    if (ok) setEditOpen(false);
  };

  return (
    <div className="space-y-4" data-testid="registros-tab">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 items-end">
        <div className="flex-1 grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Funcionário</Label>
            <Select value={filterMember} onValueChange={setFilterMember}>
              <SelectTrigger data-testid="filter-member-select"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="filter-start" />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="filter-end" />
          </div>
        </div>
        <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={exportCSV} data-testid="export-records-csv">
          <Download className="w-3 h-3" />CSV
        </Button>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden divide-y divide-border/30 rounded-lg border border-border/50 bg-card">
        {records.map(r => {
          const st = getRecordStatus(r);
          return (
            <div key={r.id} className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{r.member_name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[st.color]}`}>{st.label}</span>
              </div>
              <p className="text-xs text-muted-foreground">{fmtDate(r.clock_in)}</p>
              <div className="flex items-center gap-4 text-sm">
                <span>↑ <strong>{fmtTime(r.clock_in)}</strong></span>
                <span>↓ <strong>{fmtTime(r.clock_out)}</strong></span>
                <span className="ml-auto font-bold">{r.hours_worked?.toFixed(1) || "-"}h</span>
              </div>
              {(r.overtime_hours || 0) > 0 && <p className="text-xs text-amber-600 font-bold">+{r.overtime_hours.toFixed(1)}h extra</p>}
              {st.detail && <p className="text-[10px] text-muted-foreground italic">{st.detail}</p>}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" className="h-7 text-xs flex-1 gap-1" onClick={() => openEdit(r)}>
                  <Pencil className="w-3 h-3" />Editar
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteRecord(r.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          );
        })}
        {records.length === 0 && <p className="p-8 text-center text-muted-foreground text-sm">Nenhum registro encontrado</p>}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-card rounded-lg border border-border/50 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/50 text-muted-foreground text-xs uppercase">
              <th className="text-left p-3 font-medium">Funcionário</th>
              <th className="text-left p-3 font-medium">Data</th>
              <th className="text-left p-3 font-medium">Entrada</th>
              <th className="text-left p-3 font-medium">Saída</th>
              <th className="text-right p-3 font-medium">Horas</th>
              <th className="text-right p-3 font-medium">Extra</th>
              <th className="text-center p-3 font-medium">Status</th>
              <th className="p-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/30">
            {records.map(r => {
              const st = getRecordStatus(r);
              return (
                <tr key={r.id} className="hover:bg-muted/50" data-testid={`record-row-${r.id?.slice(-6)}`}>
                  <td className="p-3 font-medium">{r.member_name}</td>
                  <td className="p-3 text-muted-foreground">{fmtDate(r.clock_in)}</td>
                  <td className="p-3">{fmtTime(r.clock_in)}</td>
                  <td className="p-3">{fmtTime(r.clock_out)}</td>
                  <td className="p-3 text-right font-bold">{r.hours_worked?.toFixed(1) || "-"}h</td>
                  <td className="p-3 text-right">
                    {(r.overtime_hours || 0) > 0
                      ? <span className="text-amber-600 font-bold">+{r.overtime_hours.toFixed(1)}h</span>
                      : <span className="text-muted-foreground">-</span>}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[st.color]}`}>{st.label}</span>
                      {st.detail && <span className="text-[9px] text-muted-foreground max-w-[140px] leading-tight">{st.detail}</span>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500" onClick={() => openEdit(r)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => deleteRecord(r.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {records.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Nenhum registro encontrado</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{records.length} registros</p>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Corrigir Registro</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm font-medium text-muted-foreground">{editTarget?.member_name}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Entrada</Label>
                <Input type="datetime-local" value={editForm.clock_in} onChange={e => setEditForm(f => ({ ...f, clock_in: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Saída</Label>
                <Input type="datetime-local" value={editForm.clock_out} onChange={e => setEditForm(f => ({ ...f, clock_out: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observação" />
            </div>
            <div>
              <Label className="text-xs">Motivo da correção <span className="text-red-500">*</span></Label>
              <Input
                value={editForm.reason}
                onChange={e => setEditForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Ex: Funcionário esqueceu de registar saída"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button size="sm" onClick={handleEditSave} disabled={saving || !editForm.reason.trim()}>
              {saving ? "A guardar..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
